/**
 * Pre-split totals for a fund shared between two partners.
 *
 * A shared fund is stored as two linked copies, one per partner. A mirror
 * (`splitRatio == null`) stores the full amount on both copies. A split stores
 * each partner's portion. The totals are the owner's copy for a mirror, and the
 * sum of both copies for a split. When the partner's copy can't be found, the
 * total is derived from the ratio instead.
 *
 * Shared so the browser (form seeding) and the server (restoring the owner on
 * unlink) compute the same number.
 */

export const PARTNER_SPLIT_MONETARY_FIELDS = [
  'existingAssetsValue',
  'monthlyContributionAmount',
  'monthlyIncome',
  'drawdownAmount',
] as const;

export function captureOriginalMonetaryValues(source: SelfFundingSource): Record<string, number> {
  const values: Record<string, number> = {};
  PARTNER_SPLIT_MONETARY_FIELDS.forEach(field => {
    const value = source[field];
    if (typeof value === 'number') values[field] = value;
  });
  return values;
}

/**
 * Never rely on session-stored originalMonetaryValues instead of this.
 */
export function computeCombinedTotals(
  source: SelfFundingSource | undefined,
  partnerSource: SelfFundingSource | undefined,
): Record<string, number> {
  if (!source) return {};

  if (source.splitRatio == null) {
    return captureOriginalMonetaryValues(source);
  }

  if (partnerSource) {
    const result: Record<string, number> = {};
    PARTNER_SPLIT_MONETARY_FIELDS.forEach(field => {
      const a = source[field];
      const b = partnerSource[field];
      if (typeof a === 'number' || typeof b === 'number') {
        result[field] = (typeof a === 'number' ? a : 0) + (typeof b === 'number' ? b : 0);
      }
    });
    return result;
  }

  // Fractional math so monthly amounts don't amplify a rounding loss when the
  // form re-derives the yearly value.
  const ratio = source.splitRatio > 0 ? source.splitRatio / 100 : null;
  if (ratio == null) return captureOriginalMonetaryValues(source);
  const result: Record<string, number> = {};
  PARTNER_SPLIT_MONETARY_FIELDS.forEach(field => {
    const a = source[field];
    if (typeof a === 'number') {
      result[field] = a / ratio;
    }
  });
  return result;
}
